import { ChangeDetectionStrategy, Component, output, HostBinding, Signal, computed, input } from '@angular/core';
import { ConfirmMode, DialogService } from '../dialog/index.js';
import { PlatformService } from 'lib/platform/index.js';
import { TooltipService } from './tooltip.service.js';
import { IconType } from '../icons/index.js';
import { IImage } from '../ui-models.js';

type TooltipMenuItemMode = 'default' | 'custom';
type TooltipMenuItemAvatarLocation = 'start' | 'end';
type TooltipMenuItemDesign = 'default' | 'delete' | 'disabled';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'lib-tooltip-menu-item',
    templateUrl: 'tooltip-menu-item.component.html',
    standalone: false
})
export class TooltipMenuItemComponent {
	@HostBinding('style') style = 'display: block';

	public readonly title = input<string>();
	public readonly isFirst = input.required<boolean>();
	public readonly isLast = input.required<boolean>();
	public readonly removePadding = input<boolean>(false);
	public readonly removeBorder = input<boolean>(false);
	public readonly subtitle = input<string>();
	public readonly typeIcon = input<IconType>();
	public readonly routerLink = input<string>();
	public readonly confirmMode = input<ConfirmMode>();
	public readonly confirmText = input<string>();
	public readonly confirmTitle = input<string>();
	public readonly avatar = input<IImage>();
	public readonly avatarLocation = input<TooltipMenuItemAvatarLocation>('start');
	public readonly enableConfirm = input<boolean>();
	public readonly disabled = input<boolean>();
	public readonly mode = input<TooltipMenuItemMode>('default');
	public readonly itemDesign = input<TooltipMenuItemDesign>('default');

	public readonly itemClick = output<MouseEvent>();

	public readonly calculatedDesign: Signal<TooltipMenuItemDesign> = computed<TooltipMenuItemDesign>(() => {
		if (this.disabled() === true) {
			return 'disabled';
		}
		return this.itemDesign();
	});

	private readonly canUseTippy: boolean;

	constructor(
		private readonly dialogService: DialogService,
		readonly platformService: PlatformService,
		private readonly tooltipService: TooltipService
	) {
		this.canUseTippy = platformService.isBrowser;
	}

	handleClick(event: MouseEvent): void {
		if (!this.canUseTippy) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		if (this.disabled() === true) {
			// button is disabled
			return;
		}

		// hide tooltip menus when handling click
		this.tooltipService.hideAllTooltips();

		if (this.enableConfirm()) {
			this.dialogService.openConfirmationDialog({
				mode: this.confirmMode(),
				text: this.confirmText(),
				title: this.confirmTitle(),
				confirm: () => {
					// fire item click event
					this.itemClick.emit(event);
				}
			});
		} else {
			this.itemClick.emit(event);
		}
	}
}
